// ============================================================================
// rfq-attached-request.ts
// ----------------------------------------------------------------------------
// A saved project whose RFQ text is a COVERING EMAIL ("Please find the attached
// document with the details of the quote request.") while the quote request
// itself is an attached document (job 99201, 2026-10-21). Stage 1 used to put
// the email's body in the RFQ box even after it picked the attached request as
// the RFQ, so the saved project:
//   - holds no verified requested-scope rows (the email names no work), and
//   - stops Stage 3 with `incorporation-relationship-unresolved`, because the
//     email refers to "the attached document" and no technical document answers.
//
// This finds the attached request by CONTENT only (never a file name, Peter
// 2026-10-13) and builds the form that uses it as the RFQ, through the one
// receipt-minting function. It changes nothing on its own: the Stage 3 recheck
// asks the operator first (rfq-source-recheck.ts).
//
// A match needs ALL of:
//   1. the RFQ text carries a verified receipt whose source is an email;
//   2. that email text authorises no requested scope of its own;
//   3. Stage 3 holds on it, and EVERY hold is a covering-note pointer: a
//      sentence about an attached or enclosed document carrying the request or
//      its details, naming no technical document (report, drawing, plan,
//      specification, engineer, photographs...). An email that says "as per the
//      attached engineer's report" keeps its hold;
//   4. exactly one OTHER document is a complete, non-email document classified
//      as an RFQ by its content, whose text takes a verified receipt;
//   5. the email and that document carry the same labelled request number
//      (job, quote, request, order or work order number), read whole from their
//      text, so "Job 2026-0451" never matches "Quote 2026-0999" on the year.
// Anything else returns null, and nothing changes.
// ============================================================================
import { isEmailDocument } from './stored-document-text';
import { resolveDocumentClassification } from '../utils/document-classification';
import {
    deriveVerifiedRfqContentProvenance,
    isVerifiedRfqContentProvenance,
    resolveIncorporatedTechnicalAuthority,
    resolveVerifiedRfqScope,
} from '../utils/source-provenance';
import { rebindVerifiedRfqAuthority } from '../utils/rfq-authority-binding';

type LooseRecord = Record<string, unknown>;

export interface AttachedRequestMatch {
    /** The attached quote request that will become the RFQ. */
    document: LooseRecord;
    /** The covering email the RFQ text came from. */
    sourceDocument: LooseRecord;
    /** Request numbers both texts carry, digits only (e.g. ['99201']). */
    sharedReferences: string[];
}

export interface RfqSourceAdoption {
    fromDocumentId: string;
    fromDocumentName: string;
    toDocumentId: string;
    toDocumentName: string;
    sharedReferences: string[];
    /**
     * 'stage1-import': the operator chose it at a Stage 1 import (stage1-import-rfq-text.ts).
     * 'stage3-best-reading': QuoteMaker used it before a Work Method run with no
     * question (Full AI, rfq-source-recheck-runner.ts), so nobody chose it.
     * Absent: the operator's yes at the Stage 3 Recheck (v6.0.112 and earlier).
     */
    via?: 'stage1-import' | 'stage3-best-reading';
    adoptedAt: string;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const idOf = (document: unknown): string => String((document as LooseRecord | null)?.id ?? '');

// A labelled request number: "Job #99201", "Quote No. 99201", "Work Order 12345",
// "QR601670". Strict, because a loose read matched other jobs in review:
//   - the number follows its label on the SAME line, with only "No.", "#", "ID"
//     or ":" between, and no letter prefix except QR, WO or RFQ, so a strata plan
//     ("Quote Request: SP 55555"), a date ("Quote by 30/11/2026") or prose
//     ("Job for 2026") never counts. "Ref" is not a label: "Ref: SP 12345";
//   - it is read whole across hyphens (ASCII and Unicode), slashes, dots and
//     underscores, and compared as all its digits, so "Job 2026-0451" never
//     matches "Quote No. 2026-0999";
//   - a bare four-digit year is never a request number;
//   - a prefixed number may carry a hyphen ("Work Order: WO-55001").
const REQUEST_NUMBER = /\b(?:job|quote|request|work[ \t]*order|order|rfq|wo|qr)[ \t]*(?:no\.?|number|#|id)?[ \t]*[:#.]?[ \t]*#?[ \t]*((?:(?:qr|wo|rfq)[-‐-―]?)?\d+(?:[-‐-―\/._]\d+)*)(?![\d‐-―\/_-])/gi;

export function requestNumbersIn(value: unknown): Set<string> {
    const found = new Set<string>();
    for (const match of text(value).matchAll(REQUEST_NUMBER)) {
        const digits = String(match[1] || '').replace(/\D/g, '');
        if (digits.length < 4 || /^(?:19|20)\d\d$/.test(digits)) continue;
        found.add(digits);
    }
    return found;
}

// The request number an email is ABOUT: its own first Subject line only, never a
// quoted earlier thread ("> Subject: RE: ... Job #77777") or a passing mention.
export function subjectLineOf(...texts: unknown[]): string {
    for (const value of texts) {
        const subject = /^subject:[ \t]*(.+)$/im.exec(text(value));
        if (subject) return subject[1]!;
    }
    return '';
}

export function subjectRequestNumbers(...texts: unknown[]): Set<string> {
    return requestNumbersIn(subjectLineOf(...texts));
}

// Every number a request document identifies itself by, for the Stage 1
// question's cross-job guard (release review 3, 2026-10-21). Another job's
// request said "Our Ref: 55001", "Reference No. 55001", "Ticket #55001",
// "Maintenance Request MR-55001" or "Task ID 8844570" and mentioned this job's
// number only in passing ("Follows previous Job No. 12345"), and the question
// was raised. These numbers never make a match; they only stop one.
const OTHER_REFERENCE = /\b(?:ref(?:erence)?|ticket|task|mr)\b[ \t]*(?:no\.?|number|#|id)?[ \t]*[:#.-]?[ \t]*#?[ \t]*(\d+(?:[-‐-―\/._]\d+)*)(?![\d‐-―\/_-])/gi;
// A strata plan number: "SP 55555", "S/Plan 55555", "Strata Plan 55555".
const PLAN_NUMBER = /\b(?:sp|s\/plan|strata[ \t]+plan)\b[ \t]*(?:no\.?|number|#)?[ \t]*[:#.]?[ \t]*(\d{3,})(?!\d)/gi;
// A request number that only points back at another job: "Follows previous Job
// No. 12345", "as per Work Order 12345". It is not the document's own number.
const BACK_REFERENCE = /\b(?:previous|prior|earlier|former|last|original|old|follow(?:s|ing|ed)?|further[ \t]+to|related[ \t]+to|refer(?:s|ring)?[ \t]+to|see[ \t]+also|supersed(?:e|es|ed|ing)|replac(?:e|es|ed|ing)|same[ \t]+as|similar[ \t]+to|as[ \t]+per)\b[^.\n]{0,24}$/i;

const asRequestNumber = (raw: unknown): string | null => {
    const digits = String(raw || '').replace(/\D/g, '');
    return digits.length < 4 || /^(?:19|20)\d\d$/.test(digits) ? null : digits;
};

export interface RequestIdentifiers {
    /** Labelled request numbers the text names as its own, not in a back-reference. */
    own: Set<string>;
    /** Every labelled request and reference number, own or passing. */
    all: Set<string>;
    /** Strata plan numbers. */
    plans: Set<string>;
}

export function requestIdentifiersIn(value: unknown): RequestIdentifiers {
    const source = text(value);
    const ids: RequestIdentifiers = { own: new Set(), all: new Set(), plans: new Set() };
    for (const match of source.matchAll(REQUEST_NUMBER)) {
        const number = asRequestNumber(match[1]);
        if (!number) continue;
        ids.all.add(number);
        const lineStart = source.lastIndexOf('\n', match.index) + 1;
        if (!BACK_REFERENCE.test(source.slice(lineStart, match.index))) ids.own.add(number);
    }
    for (const match of source.matchAll(OTHER_REFERENCE)) {
        const number = asRequestNumber(match[1]);
        if (number) ids.all.add(number);
    }
    for (const match of source.matchAll(PLAN_NUMBER)) ids.plans.add(String(match[1]));
    return ids;
}

// A covering-note pointer: a sentence that only hands over an attached request.
// Every word must come from this vocabulary, so a sentence that also asks for
// work ("repair", "rectify"), describes the attachment as a source
// ("inspection", "assessment", "declaration", "scope") or names who wrote it
// ("from Smith Engineering") is never one, and its hold stays the builder's call.
const COVERING_NOTE_WORDS = new Set([
    'please', 'kindly', 'find', 'see', 'refer', 'to', 'attached', 'attachment', 'attachments',
    'enclosed', 'herewith', 'here', 'is', 'are', 'the', 'a', 'an', 'this', 'our', 'your',
    'with', 'of', 'for', 'containing', 'contains', 'includes', 'including', 'all',
    'details', 'detail', 'information', 'particulars', 'document', 'documents', 'file',
    'files', 'pdf', 'copy', 'form', 'quote', 'quotation', 'request', 'requests', 'rfq',
    'work', 'order', 'job', 'new', 'and',
]);
const ATTACHMENT_CUE = /\b(?:attach(?:ed|ment|ments)?|enclosed|herewith)\b/i;
const REQUEST_NOUN = /\b(?:quote|quotation|request|rfq|work[ \t]*order|job)\b/i;
// Whole covering-note sentences that real strata emails use and the word list
// does not cover, each measured on the stored and on-disk strata emails
// (2026-10-21). A sentence must match from start to end, so one that adds
// anything is not one of these. The strata plan number is the only free part.
const COVERING_NOTE_SENTENCES = [
    // Waratah Strata Management, 3 of 3 real notes (2023 to 2026).
    /^please find attached a quotation request for (?:the owners[ \t]*[-‐–—][ \t]*)?strata plan[ \t]+\d+\.?$/i,
];

export function isCoveringNotePointer(statement: unknown): boolean {
    const value = text(statement);
    if (COVERING_NOTE_SENTENCES.some(sentence => sentence.test(value.trim()))) return true;
    if (!ATTACHMENT_CUE.test(value) || !REQUEST_NOUN.test(value)) return false;
    const words = value.toLowerCase().match(/[a-z]+/g) || [];
    return words.length > 0 && words.every(word => COVERING_NOTE_WORDS.has(word));
}

// The STRICT reading of a covering-note EMAIL: once its header block, its
// salutation and its signature are set aside, every sentence left is a
// covering-note pointer or an allowlisted courtesy sentence, and at least one is
// a pointer. One other sentence (a question, an access note, "please repair the
// balcony"), before or after the sign-off, means the email says something of
// its own. Since 2026-10-21 (Peter: "Never swap silently") this decides no swap:
// the Stage 1 import question uses it to warn the operator that the email says
// more than "the request is attached" (stage1-import-rfq-text.ts).
const EMAIL_HEADER_LINE = /^(?:from|to|cc|bcc|sent|date|subject|reply-to)[ \t]*:/i;
const SALUTATION = /^(?:dear|hi|hello|hey|good[ \t]+(?:morning|afternoon|evening|day)|g'?day|greetings|to[ \t]+whom[ \t]+it[ \t]+may[ \t]+concern)\b/i;
// The salutation alone ("Hello", "Good afternoon,") or with a name and a comma
// ("Dear Sample Builder Pty Ltd,"). A salutation never asks for anything.
const SALUTATION_ONLY = /^(?:dear|hi|hello|hey|good[ \t]+(?:morning|afternoon|evening|day)|g'?day|greetings|to[ \t]+whom[ \t]+it[ \t]+may[ \t]+concern)(?:(?:[ \t]+[^\s,!?:;]+){1,6}[ \t]*,|[ \t]*[,!]?)$/i;
const ASKING_WORD = /\b(?:please|kindly|could|can|would|attach(?:ed|ment|ments)?|enclosed)\b/i;
// A salutation holds only a name ("Dear Sample Builder Pty Ltd,", "Hi team,"),
// never words about the job ("Hi Rodney urgent roof leak Unit 4,"), also when
// it wraps onto a second line ("Dear Rodney" / "Balcony leak at Unit 4,").
const NAME_WORD = /^(?:[A-Z][^\s]*|&|and|of|the)$/;
const ADDRESSEE_WORDS = new Set(['all', 'team', 'everyone', 'there', 'sir', 'madam']);
const SIGN_OFF_LINE = /^(?:(?:(?:kind|best|warm|warmest|many|with)[ \t]+)?(?:regards|thanks|thank[ \t]+you|cheers|sincerely)|yours[ \t]+(?:sincerely|faithfully|truly))[ \t]*[,.!]*$/i;
const WORK_WORD = /\b(?:repair|replac|fix|leak|attend|inspect|install|remov|rectif|investigat|waterproof|paint|seal|crack|damag)/i;
const SENTENCE_END = /(?<=[.!?])\s+/;
// Courtesy sentences a covering note may carry beside its pointer. Whole
// sentence, with only the contact's name free (one to four capitalised words).
const COURTESY_SENTENCES = [
    // Waratah Strata Management, 3 of 3 real notes (2023 to 2026).
    /^[Ii]f you have any questions, please contact (?:[A-Z][A-Za-z'’-]*[ \t]+){0,3}[A-Z][A-Za-z'’-]* during business hours\.?$/,
];
const isCourtesySentence = (sentence: string): boolean => !WORK_WORD.test(sentence)
    && COURTESY_SENTENCES.some(courtesy => courtesy.test(sentence));

// After the sign-off only the signature may follow (release review
// 2026-10-21: "Thanks, Jane" then "Also include the stairwell balustrade" lost
// the balustrade). A signature is contact lines (names, titles, companies,
// addresses, phone and fax lines, links) and standard disclaimer sentences.
// Anything else, a postscript, a quoted message, more scope, an access note or
// a deadline, still speaks, and the email stays the RFQ for the builder to
// confirm at Stage 3.
const POSTSCRIPT = /^(?:p\.?[ \t]*s|n\.?[ \t]*b)\b/i;
// A list item ("- Stairwell Balustrade", "2. Foyer lights") is never a signature line.
const LIST_ITEM = /^(?:[-*•]|\d+[.)])[ \t]+\S/;
const QUOTED_MESSAGE_LINE = /^(?:[_=-]{5,}|>|(?:from|sent|to|subject|cc|date)[ \t]*:)|\bwrote:[ \t]*$/i;
// A link, email or phone address token says nothing (a mail filter rewrites
// links as "<https://urldefense.proofpoint.com/v2/url?u=...>"), but the
// punctuation that closes it still counts: "Tuesday Or Wednesday
// https://x.example/book?" still asks.
const ADDRESS_TOKEN = /^<?(?:[a-z][a-z0-9+.-]*:\/\/|mailto:|tel:|www\.)|@/i;
const CLOSING_PUNCTUATION = /[?.,;:!]+$/;
// A shared mailbox signs with its department ("Repairs | Waratah Strata
// Management", 2 of 2 real emails from that mailbox). Only that form: the rest
// of the line names an organisation and no job or site.
const DEPARTMENT_LABEL = /^repairs[ \t]*\|[ \t]*(\S.*)$/i;
const ORGANISATION_WORD = /\b(?:strata|management|group|pty|ltd|limited|services)\b/i;
// A mail template's unfilled placeholder, as a whole line (1 of 3 real Waratah
// notes).
const SIGNATURE_PLACEHOLDER = /^\*+[ \t]*enter your signature details here[ \t]*\*+$/i;
// Contact lines are capitalised words, numbers and these joining words.
const CONTACT_JOIN_WORDS = new Set(['for', 'of', 'and', 'the', 'at', 'on', 'in', 'to', 'by']);
const SIGNATURE_ASK = /\b(?:please|kindly|could|can|would|also|include|including|need|needs|require|required|urgent|asap|note|too|as well|due|deadline|access|budget|gate|keys|tenants?|separate(?:ly)?|prices?|quotes?|quotation)\b/i;
// A capitalised line longer than a name or a title ("Stairwell Balustrade And
// Common Area Lighting") is a contact line only with contact data in it.
const CONTACT_LINE_MAX_WORDS = 5;
const CONTACT_LABEL = /^(?:ph|phone|tel|t|m|mob|mobile|fax|f|e|email|w|web|a|address|abn|acn|office)\b[ \t]*[:.]?/i;
const hasContactData = (line: string): boolean => line.replace(/\D/g, '').length >= 6
    || nameWords(line).some(token => ADDRESS_TOKEN.test(token))
    || ORGANISATION_WORD.test(line)
    || CONTACT_LABEL.test(line.trim());
const DISCLAIMER_CUE = /\b(?:liability limited by a scheme|confidential|privileged|intended (?:only )?for the (?:named )?(?:addressee|recipient)|intended recipient|received this (?:e-?mail|message|communication|transmission) in error|notify the sender|environment before printing|views (?:expressed|contained)|unauthori[sz]ed (?:use|disclosure|copying|review)|virus(?:es)?)\b/i;
// A disclaimer never names the job, the work or the site.
const DISCLAIMER_DENY = /\b(?:also|includ\w*|quot\w*|pric\w*|separate\w*|access|tenants?|owners?|unit|level|lot|site|building)\b/i;

const nameWords = (value: string): string[] => value.split(/\s+/).filter(Boolean);
// A name holds at most three words besides company and team words
// ("Dear Sample Builder Pty Ltd,"); "Hi Rodney Stairwell Balustrade Urgently," is more.
const NAME_SUFFIX_WORDS = new Set(['pty', 'ltd', 'limited', 'team', 'group', 'strata', 'builder', 'builders', 'building', 'services', 'management', 'co', 'inc', '&', 'and', 'of', 'the']);
const SALUTATION_NAME_MAX_WORDS = 3;
const isSalutation = (value: string): boolean => {
    if (!SALUTATION_ONLY.test(value) || ASKING_WORD.test(value) || WORK_WORD.test(value)) return false;
    const words = nameWords(value.replace(SALUTATION, '').replace(/[,!][ \t]*$/, ''));
    return words.every(word => NAME_WORD.test(word) || ADDRESSEE_WORDS.has(word.toLowerCase()))
        && words.filter(word => !NAME_SUFFIX_WORDS.has(word.replace(/[,.]+$/, '').toLowerCase())).length <= SALUTATION_NAME_MAX_WORDS;
};

const wordTokens = (line: string): string[] => nameWords(line).map(token => (ADDRESS_TOKEN.test(token)
    ? CLOSING_PUNCTUATION.exec(token.replace(/>+$/, ''))?.[0] || ''
    : token)).filter(Boolean);
const isContactLine = (line: string): boolean => {
    if (SIGN_OFF_LINE.test(line.trim()) || SIGNATURE_PLACEHOLDER.test(line.trim())) return true;
    const tokens = wordTokens(line);
    const rest = tokens.join(' ');
    if (rest.includes('?') || SIGNATURE_ASK.test(rest) || WORK_WORD.test(rest)) return false;
    if (tokens.filter(token => /[A-Za-z]/.test(token)).length > CONTACT_LINE_MAX_WORDS && !hasContactData(line)) return false;
    return tokens.every(token => {
        const word = token.replace(/^[^A-Za-z]+|[^A-Za-z]+$/g, '');
        return !word || /^[A-Z]/.test(word) || CONTACT_JOIN_WORDS.has(word.toLowerCase());
    });
};
const isDisclaimerSentence = (sentence: string): boolean => DISCLAIMER_CUE.test(sentence)
    && !DISCLAIMER_DENY.test(sentence)
    && !WORK_WORD.test(sentence)
    && !sentence.includes('?');
// The label is read line by line, before lines join into sentences, so another
// line never supplies its organisation. A line that is not the label keeps
// "Repairs", which still speaks; the organisation left by one that is must
// still read as a contact line.
const withoutDepartmentLabel = (line: string): string => {
    const rest = DEPARTMENT_LABEL.exec(line.trim())?.[1];
    if (!rest) return line;
    const words = wordTokens(rest).join(' ');
    return ORGANISATION_WORD.test(words) && !DISCLAIMER_DENY.test(words) ? rest : line;
};

function signatureSpeaks(signatureLines: string[]): boolean {
    const lines = signatureLines.map(withoutDepartmentLabel);
    if (lines.some(line => POSTSCRIPT.test(line) || QUOTED_MESSAGE_LINE.test(line) || LIST_ITEM.test(line.trim()))) return true;
    const paragraphs = lines.join('\n').split(/\n[ \t]*\n/)
        .map(paragraph => paragraph.split('\n').map(line => line.trim()).filter(Boolean))
        .filter(paragraph => paragraph.length > 0);
    return paragraphs.some(paragraph => {
        if (paragraph.every(isContactLine)) return false;
        // A disclaimer wraps across lines, so it is read as sentences.
        const sentences = paragraph.join(' ').replace(/\s+/g, ' ').split(SENTENCE_END)
            .map(sentence => sentence.trim())
            .filter(Boolean);
        return !sentences.every(sentence => isContactLine(sentence) || isDisclaimerSentence(sentence));
    });
}

// The Stage 1 question's warning reads tighter (release review 3, 2026-10-21):
// a short capitalised line after the signature ("Stairwell Balustrade And Foyer
// Lights", "Before Christmas Ideally", "Items:") or a greeting longer than a
// name ("Dear Rodney Gutters And Downpipes,") passes the reading above. For the
// warning, every line after the sign-off must be clearly signature: the
// sender's name right below the sign-off, a job title, a line with contact data,
// a department label or a disclaimer; and a greeting names at most two people's
// words. A wrong warning costs nothing: it only asks the operator to read.
const ROLE_WORD = /\b(?:manager|director|co-?ordinator|officer|administrator|assistant|executive|consultant|accountant|agent|secretary|supervisor|associate|partner|principal|chair(?:person)?|treasurer|representative|analyst|specialist|advis[eo]r|receptionist)\b/i;
const ROLE_LINE_MAX_WORDS = 6;
const PERSON_NAME = /^[A-Z][A-Za-z'’.-]*(?:[ \t]+[A-Z][A-Za-z'’.-]*){0,2}$/;
const SEPARATOR_LINE = /^[-_=*~.|•]+$/;
const HONORIFIC_WORDS = new Set(['mr', 'mrs', 'ms', 'miss', 'dr']);
const GREETING_NAME_MAX_WORDS = 2;
const DISCLAIMER_HEADING = /^(?:important|disclaimer|confidential(?:ity)?(?:[ \t]+(?:notice|note))?|privacy(?:[ \t]+notice)?|notice)[ \t]*[:.!-]*$/i;
const DISCLAIMER_WRAP_MIN_CHARS = 40;
// Words a name, a title, a phone, email, address or link never needs, and an
// access, timing or job note does (release review 4, 2026-10-21): "Foyer
// Lights" right under the sign-off, "Building Manager Onsite Thursdays Only",
// "Lockbox 123456 At Rear", "Strata Committee Prefers Monday Starts", and a
// disclaimer-shaped "Confidential: please note the lift is out of service
// until Friday." Release review 5 (S1): ordinary signatures must stay quiet,
// so the name line and a line with contact data or office hours are read
// against the building-element words only (never a name, street or company
// word), and month first names ("April Example", "Dear April,") are not job
// words.
const STRUCTURE_WORD = /\b(?:stair\w*|balustrad\w*|foyer\w*|lighting|roof\w*|gutter\w*|downpipe\w*|balcon\w*|window\w*|membrane\w*|garage\w*|lockbox\w*|carpark\w*|driveway\w*|ceiling\w*|bathroom\w*|scaffold\w*|leak\w*|crack\w*)\b/i;
const JOB_DETAIL = /\b(?:stair\w*|balustrad\w*|foyer\w*|lighting|roof\w*|gutter\w*|downpipe\w*|balcon\w*|window\w*|doors?|til(?:e|es|ing)|membrane\w*|garage\w*|lifts?|gates?|keys|key[ \t]*(?:safe|box|code)|lockbox\w*|carpark\w*|driveway\w*|fenc\w*|ceiling\w*|bathroom\w*|kitchen|pool|drain\w*|leak\w*|crack\w*|rust|scaffold\w*|rear|monday|tuesday|wednesday|thursday|friday|saturday|sunday|(?:mon|tues?|wed|thur?s?|fri|sat|sun)days|weekdays?|weekends?|mornings?|afternoons?|evenings?|nights?|onsite|on-site|arrival|arrive|asap|urgent\w*|christmas|easter|deadline|january|february|march|september|october|november|december|\d{1,2}(?::\d{2})?[ \t]?(?:am|pm))\b/i;
// Timing words a signature line never needs; a standard disclaimer may use them ("intended only for", "before printing").
const LINE_TIMING = /\b(?:before|after|until|only|prefer\w*|starts?|ideally)\b/i;
// Office hours: a day range ("Monday to Friday", "Mon - Fri") or a time range ("8.30AM - 5PM").
const DAY = '(?:mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)[a-z]*\\.?';
const TIME = '\\d{1,2}(?:[:.]\\d{2})?[ \\t]*(?:am|pm)';
const HOURS_RANGE = new RegExp(`\\b(?:${DAY}[ \\t]*(?:to|through|thru|-|–|—)[ \\t]*${DAY}|${TIME}[ \\t]*(?:to|-|–|—)[ \\t]*${TIME})`, 'i');
const saysJobDetail = (value: string): boolean => JOB_DETAIL.test(wordTokens(value).join(' '));
// A phone or other long number, an email address or link, or a contact label.
const hasDirectContactData = (line: string): boolean => line.replace(/\D/g, '').length >= 6
    || nameWords(line).some(token => ADDRESS_TOKEN.test(token))
    || CONTACT_LABEL.test(line.trim());

const isPlainSignatureLine = (line: string, first: boolean): boolean => {
    const value = line.trim();
    if (SIGN_OFF_LINE.test(value) || SIGNATURE_PLACEHOLDER.test(value) || SEPARATOR_LINE.test(value)) return true;
    if (!isContactLine(value)) return false;
    const words = wordTokens(value).join(' ');
    if (STRUCTURE_WORD.test(words)) return false;
    if (first && PERSON_NAME.test(value)) return true;
    if (hasDirectContactData(value) || HOURS_RANGE.test(words)) return true;
    if (JOB_DETAIL.test(words) || LINE_TIMING.test(words)) return false;
    if (ROLE_WORD.test(value) && nameWords(value).length <= ROLE_LINE_MAX_WORDS) return true;
    return hasContactData(value);
};

function signatureIsPlain(signatureLines: string[]): boolean {
    const lines = signatureLines.map(withoutDepartmentLabel).map(line => line.trim());
    const firstLine = lines.findIndex(Boolean);
    let index = 0;
    const paragraphs: Array<Array<{ line: string; at: number }>> = [[]];
    for (const line of lines) {
        if (line) paragraphs[paragraphs.length - 1]!.push({ line, at: index });
        else if (paragraphs[paragraphs.length - 1]!.length) paragraphs.push([]);
        index += 1;
    }
    return paragraphs.every((paragraph) => {
        if (paragraph.length === 0) return true;
        const sentences = paragraph.map(entry => entry.line).join(' ').replace(/\s+/g, ' ').split(SENTENCE_END)
            .map(sentence => sentence.trim())
            .filter(Boolean);
        const plainLine = (entry: { line: string; at: number }) => isPlainSignatureLine(entry.line, entry.at === firstLine);
        // A disclaimer is read as sentences, so a short line above it ("Foyer
        // Lights" / "This email is confidential.") would join its first sentence.
        // Each line is a wrapped disclaimer line, a heading or a signature line.
        if (sentences.every(isDisclaimerSentence)) {
            if (sentences.some(saysJobDetail)) return false;
            return paragraph.every(entry => entry.line.length >= DISCLAIMER_WRAP_MIN_CHARS
                || /^[a-z(]/.test(entry.line)
                || DISCLAIMER_CUE.test(entry.line)
                || DISCLAIMER_HEADING.test(entry.line)
                || plainLine(entry));
        }
        return paragraph.every(plainLine);
    });
}

const greetingNamesOnly = (value: string): boolean => {
    const name = value.replace(SALUTATION, '').replace(/[,!][ \t]*$/, '');
    return !saysJobDetail(name) && nameWords(name)
        .map(word => word.replace(/[,.]+$/, '').toLowerCase())
        .filter(word => !NAME_SUFFIX_WORDS.has(word) && !ADDRESSEE_WORDS.has(word) && !HONORIFIC_WORDS.has(word))
        .length <= GREETING_NAME_MAX_WORDS;
};

function readCoveringNoteEmail(body: unknown, plain: boolean): boolean {
    const lines = text(body).replace(/\r\n?/g, '\n').split('\n').map(line => line.trim());
    let start = 0;
    while (start < lines.length && (!lines[start] || EMAIL_HEADER_LINE.test(lines[start]!))) start += 1;
    const signOff = lines.findIndex((line, index) => index >= start && SIGN_OFF_LINE.test(line));
    const end = signOff < 0 ? lines.length : signOff;
    if (signatureSpeaks(lines.slice(end + 1))) return false;
    if (plain && !signatureIsPlain(lines.slice(end + 1))) return false;

    const paragraphs = lines.slice(start, end).join('\n').split(/\n[ \t]*\n/)
        .map(paragraph => paragraph.split('\n').filter(Boolean))
        .filter(paragraph => paragraph.length > 0);
    const first = paragraphs[0];
    if (first && SALUTATION.test(first[0]!)) {
        // The salutation may wrap ("Dear Sample" / "Builder Pty Ltd,").
        const taken = [1, 2, 3].find(count => count <= first.length
            && isSalutation(first.slice(0, count).join(' ')));
        if (taken && plain && !greetingNamesOnly(first.slice(0, taken).join(' '))) return false;
        if (taken) paragraphs[0] = first.slice(taken);
    }
    const sentences = paragraphs
        .map(paragraph => paragraph.join(' ').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .flatMap(paragraph => paragraph.split(SENTENCE_END))
        .map(sentence => sentence.trim())
        .filter(Boolean);
    return sentences.some(isCoveringNotePointer)
        && sentences.every(sentence => isCoveringNotePointer(sentence) || isCourtesySentence(sentence));
}

export function isCoveringNoteEmailBody(body: unknown): boolean {
    return readCoveringNoteEmail(body, false);
}

/** The strict reading with a plain signature and a name-only greeting: the question warns when this is false. */
export function coveringNoteSaysNothingMore(body: unknown): boolean {
    return readCoveringNoteEmail(body, true);
}

// ── Whether a Stage 1 import ASKS (Peter, 2026-10-21) ─────────────────────────
// "When an imported email looks like a covering note, ask the operator once at
// import whether to use the attached request as the RFQ, showing what would be
// set aside. Never swap silently." Broad on purpose: a wrong guess costs one
// question, never text, because the question shows every email in full. It
// reads the top message only, from the header block to the first sign-off,
// end-of-message line or quoted message; what follows is shown, not judged.
const BROAD_SIGN_OFF = /^(?:(?:(?:kind|best|warm|warmest|many|with)[ \t]+)?(?:regards|thanks|thank[ \t]+you|cheers|sincerely)|yours[ \t]+(?:sincerely|faithfully|truly))\b[^.?!]{0,40}$/i;
const END_OF_MESSAGE = /^(?:sent from my\b|get outlook for\b|-{2,}[ \t]*(?:original message|forwarded message))/i;
const BROAD_GREETING_END = /[,:–—-][ \t]*$/;
// Words a covering-note pointer may also use when the import only asks: "for
// the above property", "for your attention", "for your review", "See attached."
const BROAD_POINTER_WORDS = new Set([
    'above', 'property', 'attention', 'review', 'regarding', 're', 'as', 'per', 'discussed',
    'requested', 'you', 'i', 'we', 'have', 'has', 'been', 'sent', 'under', 'separate', 'cover',
    'on', 'behalf', 'owners', 'strata', 'plan', 'sp', 'at', 'reference', 'ref',
]);
const BROAD_COURTESY = [
    /^(?:many[ \t]+)?thanks(?:[ \t]+again)?[.!]?$/i,
    /^thank[ \t]+you(?:[ \t]+(?:very much|kindly|in advance))?[.!]?$/i,
    /^(?:please[ \t]+)?(?:let[ \t]+(?:me|us)[ \t]+know|do[ \t]+not[ \t]+hesitate[ \t]+to[ \t]+(?:contact|call)[ \t]+(?:me|us)|feel[ \t]+free[ \t]+to[ \t]+(?:contact|call)[ \t]+(?:me|us))(?:[ \t]+if[ \t]+you[ \t]+(?:have|need)[ \t]+any[ \t]+(?:questions|queries|further information))?[.!]?$/i,
    /^(?:if|should)[ \t]+you[ \t]+(?:have|require)[ \t]+any[ \t]+(?:questions|queries|further information),?[ \t]+(?:please[ \t]+)?(?:do[ \t]+not[ \t]+hesitate[ \t]+to[ \t]+|don't[ \t]+hesitate[ \t]+to[ \t]+|feel[ \t]+free[ \t]+to[ \t]+)?(?:contact|call|email)[ \t]+(?:me|us|the office|our office)[.!]?$/i,
    /^we[ \t]+look[ \t]+forward[ \t]+to[ \t]+(?:receiving[ \t]+your[ \t]+(?:quote|quotation)|hearing[ \t]+from[ \t]+you)[.!]?$/i,
    /^please[ \t]+(?:acknowledge|confirm)[ \t]+receipt(?:[ \t]+of[ \t]+this[ \t]+(?:email|request))?[.!]?$/i,
];
const isBroadPointer = (sentence: string): boolean => {
    if (isCoveringNotePointer(sentence)) return true;
    if (!ATTACHMENT_CUE.test(sentence)) return false;
    const words = sentence.toLowerCase().match(/[a-z]+/g) || [];
    return words.length > 0 && words.every(word => COVERING_NOTE_WORDS.has(word) || BROAD_POINTER_WORDS.has(word));
};
const isBroadCourtesy = (sentence: string): boolean => !WORK_WORD.test(sentence)
    && (isCourtesySentence(sentence) || BROAD_COURTESY.some(courtesy => courtesy.test(sentence.trim())));

export function looksLikeCoveringNoteEmail(body: unknown): boolean {
    const lines = text(body).replace(/\r\n?/g, '\n').split('\n').map(line => line.trim());
    let start = 0;
    while (start < lines.length && (!lines[start] || EMAIL_HEADER_LINE.test(lines[start]!))) start += 1;
    let end = lines.findIndex((line, index) => index >= start && (BROAD_SIGN_OFF.test(line.replace(/[,.!]+$/, ''))
        || END_OF_MESSAGE.test(line)
        || QUOTED_MESSAGE_LINE.test(line)));
    if (end < 0) end = lines.length;
    const message = lines.slice(start, end);
    // The greeting: its line, or the lines it wraps over up to its comma ("Dear
    // Sample" / "Builder Pty Ltd,"), or the part of its line before a comma.
    const first = message.findIndex(Boolean);
    if (first >= 0 && SALUTATION.test(message[first]!)) {
        const line = message[first]!;
        // A comma, a colon or a spaced dash ("Hi Rodney -"), never the hyphen in "Mary-Jane".
        const delimiter = /,|:|[ \t][–—-](?=[ \t]|$)/.exec(line);
        if (delimiter) {
            message[first] = line.slice(delimiter.index + delimiter[0].length).trim();
        } else if (!/[.?!]/.test(line)) {
            const wrapEnd = [1, 2].map(offset => first + offset)
                .find(index => index < message.length && BROAD_GREETING_END.test(message[index]!) && !/[.?!]/.test(message[index]!));
            for (let index = first; index <= (wrapEnd ?? first); index += 1) message[index] = '';
        }
    }
    const sentences = message.join('\n').split(/\n[ \t]*\n/)
        .map(paragraph => paragraph.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .flatMap(paragraph => paragraph.split(SENTENCE_END))
        .map(sentence => sentence.trim())
        .filter(Boolean);
    return sentences.some(isBroadPointer)
        && sentences.every(sentence => isBroadPointer(sentence) || isBroadCourtesy(sentence));
}

/**
 * An email document's full text for the operator to read: everything after its
 * header block, including what follows the sign-off and any quoted or forwarded
 * message (Stage 1's clean body drops those).
 */
export function emailTextForReview(document: unknown): string {
    const lines = text((document as LooseRecord | null)?.content).replace(/\r\n?/g, '\n').split('\n');
    let index = 0;
    while (index < lines.length && (!lines[index]!.trim() || /^(?:subject|from|to|date)[ \t]*:/i.test(lines[index]!.trim()))) index += 1;
    if (lines[index]?.trim() === '---') index += 1;
    return lines.slice(index).join('\n').trim();
}

function isCompleteDocument(document: LooseRecord): boolean {
    return document.contentComplete === true
        && document.contentTruncated !== true
        && document.extractionFailed !== true
        && text(document.content).trim().length > 30;
}

export function readsAsRequestByContent(document: LooseRecord): boolean {
    return resolveDocumentClassification(document).classification === 'rfq';
}

/** The attached quote request a covering-email RFQ points to, or null. */
export function findAttachedRequestForCoverEmail(
    formData: LooseRecord | null | undefined,
    documents: unknown[] | null | undefined,
    preprocessedFiles: unknown[] = [],
): AttachedRequestMatch | null {
    if (!formData || typeof formData !== 'object' || !Array.isArray(documents)) return null;
    const rfqContent = text(formData.rfqContent);
    if (!rfqContent.trim()) return null;
    const provenance = formData.rfqContentProvenance as LooseRecord | undefined;
    if (!isVerifiedRfqContentProvenance(provenance, rfqContent)) return null;
    // An email that authorises scope of its own is the request, not a cover note.
    if (resolveVerifiedRfqScope(formData)) return null;

    const rows = documents.filter((row): row is LooseRecord => !!row && typeof row === 'object');
    const sourceDocument = rows.find(row => idOf(row) === String(provenance.sourceId));
    if (!sourceDocument || !isEmailDocument(sourceDocument)) return null;

    // Only a covering note: Stage 3 holds, and every hold points at an attached
    // request. A hold on a named technical source is the builder's decision.
    const resolution = resolveIncorporatedTechnicalAuthority({ formData, documents: rows, preprocessedFiles });
    if (resolution.complete || resolution.holds.length === 0) return null;
    if (!resolution.holds.every(hold => hold.code === 'incorporation-relationship-unresolved'
        && isCoveringNotePointer(hold.referenceStatement))) return null;

    const candidates = rows.filter(row => row !== sourceDocument
        && !isEmailDocument(row)
        && isCompleteDocument(row)
        && readsAsRequestByContent(row));
    if (candidates.length !== 1) return null;
    const document = candidates[0]!;
    // Offered only when its text would take a verified receipt on that document.
    const receipt = deriveVerifiedRfqContentProvenance({ rfqContent: text(document.content), documents: rows, preprocessedFiles });
    if (!receipt || String(receipt.sourceId) !== idOf(document)) return null;

    const emailNumbers = subjectRequestNumbers(sourceDocument.content, rfqContent);
    const requestNumbers = requestNumbersIn(document.content);
    const sharedReferences = [...emailNumbers].filter(number => requestNumbers.has(number)).sort();
    if (sharedReferences.length === 0) return null;

    return { document, sourceDocument, sharedReferences };
}

/**
 * The form with the attached request as its RFQ: the request's own text, the
 * receipts minted from it by the one shared function, and an audit record. Null
 * when the receipt does not bind to that document, so nothing half-done is saved.
 */
export function adoptAttachedRequestFormData(
    formData: LooseRecord,
    documents: unknown[],
    preprocessedFiles: unknown[],
    match: AttachedRequestMatch,
    adoptedAt: string,
    via?: RfqSourceAdoption['via'],
): LooseRecord | null {
    const adoption: RfqSourceAdoption = {
        fromDocumentId: idOf(match.sourceDocument),
        fromDocumentName: text(match.sourceDocument.name),
        toDocumentId: idOf(match.document),
        toDocumentName: text(match.document.name),
        sharedReferences: [...match.sharedReferences],
        adoptedAt,
        ...(via ? { via } : {}),
    };
    const next: LooseRecord = rebindVerifiedRfqAuthority(
        { ...formData, rfqContent: text(match.document.content), rfqSourceAdoption: adoption } as LooseRecord,
        documents,
        preprocessedFiles,
    );
    const provenance = next.rfqContentProvenance as LooseRecord | undefined;
    if (!isVerifiedRfqContentProvenance(provenance, next.rfqContent)) return null;
    if (String(provenance.sourceId) !== idOf(match.document)) return null;
    return next;
}
